import { inject, injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { IAnalyticsGatewayToken, type IAnalyticsGateway } from '@/shared/analytics/AnalyticsGateway';
import { findTopicInSurface } from './AnalyticsSurface';
import { SurfaceRegistry } from './SurfaceRegistry';
import type { TopicDefinition } from './TopicCatalogTypes';
import { buildDriftTurns, sumCost, sumDriftCost, sumDriftTokens, sumTokens, type DriftTurn } from './DriftMetrics';
import type { AnalyticsFilter } from '@/shared/analytics/AnalyticsTypes';

const USER_RECORD_LIMIT = 1000;

export interface DriftUserSummary {
    prompts: number;
    sessions: number;
    topics: number;
    totalTokens: number;
    driftTokens: number;
    /** Allocated $ across all turns; 0 when no cost data covers them. */
    totalCostUsd: number;
    /** Allocated $ burned on drifted turns; 0 when no cost data covers them. */
    driftCostUsd: number;
}

const EMPTY_SUMMARY: DriftUserSummary = { prompts: 0, sessions: 0, topics: 0, totalTokens: 0, driftTokens: 0, totalCostUsd: 0, driftCostUsd: 0 };

/**
 * L2 view model for the person drill-in: loads one person's turns and flags each
 * for drift against its session's dominant topic, ready to group by session then
 * topic in the grid.
 */
@injectable()
export class SessionDriftUserViewModel extends BaseViewModel {
    definition: TopicDefinition | null = null;
    userId: string | null = null;
    turns: DriftTurn[] = [];
    summary: DriftUserSummary = EMPTY_SUMMARY;
    notFound = false;
    loading = false;
    errorMessage: string | null = null;

    constructor(
        @inject(IAnalyticsGatewayToken) private gateway: IAnalyticsGateway,
        @inject(SurfaceRegistry) private registry: SurfaceRegistry,
    ) {
        super();
    }

    async load(surfaceId: string, topicId: string | undefined, userId: string | undefined, ambientFilters: AnalyticsFilter[] = []): Promise<void> {
        const surface = this.registry.find(surfaceId);
        const definition = surface === undefined ? undefined : findTopicInSurface(surface, topicId);
        if (definition === undefined || userId === undefined || userId.length === 0) {
            this.notFound = true;
            this.notifyChange();
            return;
        }

        this.notFound = false;
        this.definition = definition;
        this.userId = userId;
        this.turns = [];
        this.summary = EMPTY_SUMMARY;
        this.loading = true;
        this.errorMessage = null;
        this.notifyChange();

        try {
            const result = await this.gateway.records({
                source: 'interactions',
                filters: [...ambientFilters, { field: 'user_id', values: [userId] }],
                orderBy: { column: 'timestamp', direction: 'desc' },
                limit: USER_RECORD_LIMIT,
            });
            const turns = buildDriftTurns(result.records);
            this.turns = turns;
            this.summary = {
                prompts: turns.length,
                sessions: new Set(turns.map((t) => t.sessionId)).size,
                topics: new Set(turns.map((t) => t.topic)).size,
                totalTokens: sumTokens(turns),
                driftTokens: sumDriftTokens(turns),
                totalCostUsd: sumCost(turns),
                driftCostUsd: sumDriftCost(turns),
            };
        } catch (err) {
            this.errorMessage = err instanceof Error ? err.message : String(err);
        } finally {
            this.loading = false;
            this.notifyChange();
        }
    }
}
